import { create } from 'zustand';
import type { PresenceStatus, PublicUser } from '@clanfire/shared';

/**
 * Canonical cache of user profiles. Messages and member lists carry user
 * snapshots; components prefer this cache so profile edits show up everywhere.
 */
interface UsersState {
  users: Record<string, PublicUser>;
  upsert: (users: Array<PublicUser | null | undefined>) => void;
  reset: () => void;
}

/** Shallow equality over every profile field (so any profile edit re-renders). */
function sameUser(a: PublicUser, b: PublicUser): boolean {
  const keys = Object.keys(b) as Array<keyof PublicUser>;
  return keys.length === Object.keys(a).length && keys.every((k) => a[k] === b[k]);
}

export const useUsers = create<UsersState>((set) => ({
  users: {},
  upsert: (incoming) =>
    set((state) => {
      let changed = false;
      const users = { ...state.users };
      for (const u of incoming) {
        if (!u) continue;
        const prev = users[u.id];
        if (prev && sameUser(prev, u)) continue;
        users[u.id] = u;
        changed = true;
      }
      return changed ? { users } : state;
    }),
  reset: () => set({ users: {} }),
}));

export function useUser(id: string | null | undefined, fallback?: PublicUser | null): PublicUser | null {
  const cached = useUsers((s) => (id ? s.users[id] : undefined));
  return cached ?? fallback ?? null;
}

interface PresenceState {
  statuses: Record<string, PresenceStatus>;
  /** When each user's status was last set by a live socket event. */
  liveAt: Record<string, number>;
  /** Applies a live `presence:update` event. */
  set: (userId: string, status: PresenceStatus) => void;
  /**
   * Applies a snapshot from an HTTP response. Entries updated live *after* the
   * request was sent are kept, so a slow response can't overwrite fresher state.
   */
  mergeSnapshot: (statuses: Record<string, PresenceStatus>, requestedAt: number) => void;
  reset: () => void;
}

export const usePresence = create<PresenceState>((set) => ({
  statuses: {},
  liveAt: {},
  set: (userId, status) =>
    set((s) => ({
      statuses: s.statuses[userId] === status ? s.statuses : { ...s.statuses, [userId]: status },
      liveAt: { ...s.liveAt, [userId]: Date.now() },
    })),
  mergeSnapshot: (statuses, requestedAt) =>
    set((s) => {
      const next = { ...s.statuses };
      for (const [userId, status] of Object.entries(statuses)) {
        if ((s.liveAt[userId] ?? 0) <= requestedAt) next[userId] = status;
      }
      return { statuses: next };
    }),
  reset: () => set({ statuses: {}, liveAt: {} }),
}));

export function usePresenceOf(userId: string | null | undefined): PresenceStatus {
  return usePresence((s) => (userId ? (s.statuses[userId] ?? 'offline') : 'offline'));
}
